import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../components/context/AuthContext';
import { useToast } from '../../../components/context/ToastContext';
import repositoryContainer from '../../../services/RepositoryContainer';
import { normalizeRequestStatus } from '../../../components/request-builder/requestBuilderUtils';
import RequestStatusBadge from '../../../components/request-builder/RequestStatusBadge';
import { useNotifications } from '../../../components/context/NotificationContext';
import { WorkflowDecisionForm } from '../../my-tasks/components/WorkflowTaskDetailsModal';
import { needsPriceFinalization } from '../../../utils/pricingUtils';

const getApiErrorMessage = (error, fallback) => {
  if (error?.data?.message) return error.data.message;
  if (typeof error?.data === 'string' && error.data.trim()) return error.data;

  const validationErrors = error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return fallback;
};

const getStageState = (instance, stage) => {
  const finalDecision = [...(instance.decisions || [])]
    .reverse()
    .find((decision) => (
      decision.stageOrder === stage.order &&
      ['approved', 'rejected'].includes(String(decision.decision || '').toLowerCase())
    ));

  if (finalDecision) return String(finalDecision.decision).toLowerCase();
  if (
    String(instance.status || '').toLowerCase() === 'inprogress' &&
    stage.order === instance.currentStageOrder
  ) return 'current';
  return 'pending';
};

const StageMarker = ({ state, order, compact = false }) => {
  const markerSize = compact ? 'h-6 w-6' : 'h-8 w-8';
  const iconSize = compact ? 12 : 16;

  if (state === 'approved') {
    return (
      <span className={`relative z-10 flex ${markerSize} flex-shrink-0 items-center justify-center rounded-full bg-success text-white`}>
        <Icon name="Check" size={iconSize} />
      </span>
    );
  }

  if (state === 'rejected') {
    return (
      <span className={`relative z-10 flex ${markerSize} flex-shrink-0 items-center justify-center rounded-full bg-error text-white`}>
        <Icon name="X" size={iconSize} />
      </span>
    );
  }

  return (
    <span className={`relative z-10 flex ${markerSize} flex-shrink-0 items-center justify-center rounded-full border text-xs font-caption font-semibold ${
      state === 'current'
        ? compact
          ? 'border-primary bg-card text-primary'
          : 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-muted-foreground'
    }`}>
      {order}
    </span>
  );
};

const formatWorkflowDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GH', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const STAGE_STATE_LABELS = {
  approved: 'Approved',
  rejected: 'Rejected',
  current: 'Current stage',
  pending: 'Pending',
};

const ActiveWorkflow = ({ instance }) => {
  const stages = [...(instance.stages || [])].sort((first, second) => first.order - second.order);
  const [expandedStageOrder, setExpandedStageOrder] = useState(null);
  const expandedStage = stages.find((stage) => stage.order === expandedStageOrder);
  const expandedStageState = expandedStage ? getStageState(instance, expandedStage) : null;
  const expandedStageDecisions = expandedStage
    ? (instance.decisions || []).filter((decision) => decision.stageOrder === expandedStage.order)
    : [];

  useEffect(() => {
    setExpandedStageOrder(null);
  }, [instance.id]);

  const toggleStage = (stageOrder) => {
    setExpandedStageOrder((current) => current === stageOrder ? null : stageOrder);
  };

  return (
    <section>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Workflow</h3>
        <RequestStatusBadge status={instance.status} showIcon={false} />
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-border/60 pb-3">
          <p className="truncate font-heading text-base font-semibold text-foreground">{instance.workflowName}</p>
          <span className="flex-shrink-0 text-xs text-muted-foreground font-caption">
            {stages.length} {stages.length === 1 ? 'stage' : 'stages'}
          </span>
        </div>

        <ol className="mt-4 space-y-0 md:hidden">
          {stages.map((stage, index) => {
            const state = getStageState(instance, stage);
            const isExpanded = expandedStageOrder === stage.order;
            return (
              <li key={stage.id || `${instance.id}-${stage.order}`} className="relative pb-3 last:pb-0">
                {index < stages.length - 1 && (
                  <span className="absolute left-[15px] top-8 h-[calc(100%-0.5rem)] w-px bg-border" aria-hidden="true" />
                )}
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() => toggleStage(stage.order)}
                  className="relative z-10 flex w-full items-start gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <StageMarker state={state} order={index + 1} />
                  <div className="min-w-0 flex-1 pt-0.5">
                    <p className={`truncate text-sm font-semibold font-caption ${isExpanded ? 'text-primary' : 'text-foreground'}`}>{stage.name}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground font-caption">{stage.assignedUserName}</p>
                  </div>
                  <Icon name="ChevronDown" size={15} className={`mt-2 flex-shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              </li>
            );
          })}
        </ol>

        <div className="mt-3 hidden overflow-x-auto pb-1 pt-2 md:block">
          <ol
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(9rem, 1fr))`,
              minWidth: `${Math.max(stages.length * 144, 288)}px`,
            }}
          >
            {stages.map((stage, index) => {
              const state = getStageState(instance, stage);
              const previousState = index > 0 ? getStageState(instance, stages[index - 1]) : 'pending';
              const isExpanded = expandedStageOrder === stage.order;
              const leftConnectorClass = previousState === 'approved'
                ? 'bg-success'
                : previousState === 'rejected' ? 'bg-error' : 'bg-border';
              const rightConnectorClass = state === 'approved'
                ? 'bg-success'
                : state === 'rejected' ? 'bg-error' : 'bg-border';
              return (
                <li key={stage.id || `${instance.id}-${stage.order}`} className="relative min-w-0">
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    onClick={() => toggleStage(stage.order)}
                    className="relative z-10 flex w-full min-w-0 flex-col items-center text-center focus-visible:outline-none"
                  >
                    <span className="flex w-full items-center" aria-hidden="true">
                      <span className={`h-0.5 flex-1 rounded-full ${index === 0 ? 'invisible' : leftConnectorClass}`} />
                      <span className={`mx-2 flex-shrink-0 rounded-full ${isExpanded ? 'ring-2 ring-primary/25 ring-offset-1 ring-offset-[var(--color-card)]' : ''}`}>
                        <StageMarker state={state} order={index + 1} compact />
                      </span>
                      <span className={`h-0.5 flex-1 rounded-full ${index === stages.length - 1 ? 'invisible' : rightConnectorClass}`} />
                    </span>
                    <span className={`mt-1.5 max-w-full truncate px-2 text-[11px] font-medium font-caption ${isExpanded ? 'text-primary' : 'text-foreground'}`}>{stage.name}</span>
                    <span className="max-w-full truncate px-2 text-[10px] text-muted-foreground font-caption">{stage.assignedUserName}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        {expandedStage && (
          <div className="mt-4 rounded-lg border border-border/70 bg-card/80 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-foreground font-caption">{expandedStage.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground font-caption">Assigned to {expandedStage.assignedUserName}</p>
              </div>
              <span className="rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground font-caption">
                {STAGE_STATE_LABELS[expandedStageState] || 'Pending'}
              </span>
            </div>

            {expandedStageDecisions.length > 0 ? (
              <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
                {expandedStageDecisions.map((decision) => (
                  <div key={decision.id || `${decision.stageOrder}-${decision.createdAt}`} className="text-xs font-caption">
                    <p className="font-medium capitalize text-foreground">
                      {decision.decision} by {decision.decidedByName || 'Team member'}
                    </p>
                    {decision.createdAt && <p className="mt-0.5 text-muted-foreground">{formatWorkflowDate(decision.createdAt)}</p>}
                    {decision.comment && <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{decision.comment}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-3 border-t border-border/60 pt-3 text-xs text-muted-foreground font-caption">
                No decisions or comments have been recorded for this stage.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const RequestWorkflowSection = ({ request, onRequestUpdated }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { lastWorkflowEvent, refreshAssignedTaskCount } = useNotifications();
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');
  const isPending = normalizeRequestStatus(request?.status) === 'pending';
  const [instance, setInstance] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isProcessingDecision, setIsProcessingDecision] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionResult, setActionResult] = useState(null);
  const locallyStartedInstanceId = useRef(null);

  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.id === selectedWorkflowId),
    [selectedWorkflowId, workflows]
  );
  const orderedInstanceStages = useMemo(
    () => [...(instance?.stages || [])].sort((first, second) => first.order - second.order),
    [instance?.stages]
  );
  const finalStageOrder = orderedInstanceStages.reduce(
    (highest, stage) => Math.max(highest, Number(stage.order) || 0),
    0
  );
  const approvalDisabledReason = needsPriceFinalization(request)
    && finalStageOrder > 0
    && Number(instance?.currentStageOrder) === finalStageOrder
    ? 'Every price-range item must have a final price before the final workflow approval.'
    : '';

  const workflowOptions = useMemo(
    () => workflows.map((workflow) => ({
      value: workflow.id,
      label: `${workflow.name} (${workflow.stages?.length || 0} ${workflow.stages?.length === 1 ? 'stage' : 'stages'})`,
    })),
    [workflows]
  );

  const loadInstance = async (instanceId, { preserveCurrent = false } = {}) => {
    if (!instanceId) return;
    if (!preserveCurrent) setIsLoading(true);
    setError('');
    try {
      const response = await repositoryContainer.workflowInstance.getById(instanceId);
      setInstance(response);
      if (response?.bookingRequest) onRequestUpdated?.(response.bookingRequest);
      return response;
    } catch (loadError) {
      if (preserveCurrent) {
        addToast('The latest workflow details could not be refreshed.', 'info');
      } else {
        setError(getApiErrorMessage(loadError, 'Workflow details could not be loaded.'));
      }
      return null;
    } finally {
      if (!preserveCurrent) setIsLoading(false);
    }
  };

  const loadAvailableWorkflows = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await repositoryContainer.workflow.list();
      setWorkflows(Array.isArray(response) ? response : []);
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, 'Available workflows could not be loaded.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (
        request?.workflowInstanceId &&
        locallyStartedInstanceId.current === request.workflowInstanceId
      ) {
        locallyStartedInstanceId.current = null;
        return;
      }

      setInstance(null);
      setWorkflows([]);
      setSelectedWorkflowId('');
      setError('');
      setActionError('');
      setActionResult(null);

      if (request?.workflowInstanceId) {
        setIsLoading(true);
        try {
          const response = await repositoryContainer.workflowInstance.getById(request.workflowInstanceId);
          if (!isActive) return;
          setInstance(response);
          if (response?.bookingRequest) onRequestUpdated?.(response.bookingRequest);
        } catch (loadError) {
          if (isActive) setError(getApiErrorMessage(loadError, 'Workflow details could not be loaded.'));
        } finally {
          if (isActive) setIsLoading(false);
        }
        return;
      }

      if (isAdmin && isPending) {
        setIsLoading(true);
        try {
          const response = await repositoryContainer.workflow.list();
          if (!isActive) return;
          setWorkflows(Array.isArray(response) ? response : []);
        } catch (loadError) {
          if (isActive) setError(getApiErrorMessage(loadError, 'Available workflows could not be loaded.'));
        } finally {
          if (isActive) setIsLoading(false);
        }
      }
    };

    load();
    return () => { isActive = false; };
  }, [isAdmin, request?.id, request?.workflowInstanceId]);

  useEffect(() => {
    if (!lastWorkflowEvent || !request?.workflowInstanceId) return;
    const eventInstanceId = lastWorkflowEvent.data?.workflowInstanceId
      ?? lastWorkflowEvent.data?.WorkflowInstanceId;
    const eventSubjectId = lastWorkflowEvent.data?.subjectId
      ?? lastWorkflowEvent.data?.SubjectId
      ?? lastWorkflowEvent.data?.bookingRequestId
      ?? lastWorkflowEvent.data?.BookingRequestId;
    if (
      eventInstanceId === request.workflowInstanceId
      || (lastWorkflowEvent.eventName === 'BookingRequestPriceFinalized' && eventSubjectId === request.id)
    ) {
      loadInstance(request.workflowInstanceId, { preserveCurrent: true });
    }
  }, [lastWorkflowEvent?.sequence]);

  const handleStart = async () => {
    if (!selectedWorkflow) return;
    setIsStarting(true);
    setError('');
    try {
      const result = await repositoryContainer.bookingRequest.startWorkflow(request.id, selectedWorkflow.id);
      const syntheticInstance = {
        id: result.workflowInstanceId,
        workflowId: selectedWorkflow.id,
        workflowName: result.workflowName,
        status: 'InProgress',
        currentStageOrder: result.currentStageOrder,
        currentStage: {
          name: result.currentStageName,
          order: result.currentStageOrder,
          assignedUserId: result.assignedToUserId,
          assignedUserName: result.assignedToUserName,
        },
        stages: selectedWorkflow.stages || [],
        decisions: [],
      };
      setInstance(syntheticInstance);
      locallyStartedInstanceId.current = result.workflowInstanceId;
      onRequestUpdated?.({ ...request, workflowInstanceId: result.workflowInstanceId });
      addToast(`${result.workflowName} started at ${result.currentStageName}.`, 'success');
      await loadInstance(result.workflowInstanceId, { preserveCurrent: true });
    } catch (startError) {
      setError(getApiErrorMessage(startError, 'The workflow could not be started.'));
    } finally {
      setIsStarting(false);
    }
  };

  const handleDecision = async (decision, comment) => {
    if (!instance?.id) return false;
    setIsProcessingDecision(true);
    setActionError('');
    setActionResult(null);
    try {
      const result = await repositoryContainer.bookingRequest.processWorkflowDecision(instance.id, decision, comment);
      let message = 'Decision recorded successfully.';
      if (result.isComplete) message = 'The final stage was approved and the request is complete.';
      else if (result.isRejected) message = 'The request was rejected and the workflow is closed.';
      else if (result.nextStageName) message = `Advanced to ${result.nextStageName}${result.nextAssigneeName ? `, assigned to ${result.nextAssigneeName}` : ''}.`;
      else if (decision === 'commented') message = 'Your comment was added to the current stage.';

      setActionResult({ decision, message });
      await Promise.all([
        loadInstance(instance.id, { preserveCurrent: true }),
        refreshAssignedTaskCount(),
      ]);
      return true;
    } catch (decisionError) {
      setActionError(getApiErrorMessage(decisionError, 'The decision could not be processed.'));
      return false;
    } finally {
      setIsProcessingDecision(false);
    }
  };

  if (!request?.workflowInstanceId && !(isAdmin && isPending)) return null;

  if (isLoading) {
    return (
      <section>
        <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Workflow</h3>
        <div className="h-32 animate-pulse rounded-xl bg-muted/40" />
      </section>
    );
  }

  if (instance) {
    return (
      <div className="space-y-4">
        <ActiveWorkflow instance={instance} />

        {actionResult && (
          <div className={`rounded-xl border p-4 text-sm font-caption ${
            actionResult.decision === 'rejected'
              ? 'border-error/20 bg-error/10 text-error'
              : actionResult.decision === 'commented'
                ? 'border-primary/20 bg-primary/10 text-primary'
                : 'border-success/20 bg-success/10 text-success'
          }`}>
            {actionResult.message}
          </div>
        )}

        {actionError && (
          <div className="flex items-start gap-2 rounded-xl border border-error/20 bg-error/10 p-4">
            <Icon name="AlertCircle" size={18} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
            <p className="text-sm text-error font-caption">{actionError}</p>
          </div>
        )}

        {instance.canCurrentUserAct && (
          <WorkflowDecisionForm
            onSubmit={handleDecision}
            isSubmitting={isProcessingDecision}
            approvalDisabledReason={approvalDisabledReason}
          />
        )}
      </div>
    );
  }

  if (request?.workflowInstanceId) {
    return (
      <section>
        <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Workflow</h3>
        <div className="rounded-xl border border-error/20 bg-error/5 p-5 text-center">
          <Icon name="AlertCircle" size={28} color="var(--color-error)" className="mx-auto mb-2" />
          <p className="text-sm text-error font-caption">{error || 'Workflow details are unavailable.'}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-4"
            iconName="RefreshCw"
            onClick={() => loadInstance(request.workflowInstanceId)}
          >
            Try Again
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Workflow</h3>
      <div className="rounded-xl bg-muted/20 p-4 md:p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon name="Workflow" size={21} />
          </div>
          <div>
            <h4 className="font-heading font-semibold text-foreground">Start a workflow</h4>
            <p className="mt-1 text-sm text-muted-foreground font-caption">
              Select the approval process this request should follow. Once started, the workflow is preserved with the request.
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-error/20 bg-error/10 p-3">
            <Icon name="AlertCircle" size={17} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
            <p className="text-sm text-error font-caption">{error}</p>
          </div>
        )}

        {workflows.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-border bg-card p-4 text-center">
            <p className="text-sm text-muted-foreground font-caption">
              {error ? 'Unable to confirm which workflows are available.' : 'No configured workflows are available.'}
            </p>
            <div className="mt-3 flex flex-col justify-center gap-2 sm:flex-row">
              {error && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={loadAvailableWorkflows}
                >
                  Try Again
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconName="Settings"
                onClick={() => navigate('/workflow-management')}
              >
                Configure Workflows
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <Select
              label="Workflow"
              value={selectedWorkflowId}
              options={workflowOptions}
              onChange={setSelectedWorkflowId}
              placeholder="Select a workflow"
              disabled={isStarting}
            />

            {selectedWorkflow && (
              <div className="rounded-lg bg-card/80 p-4">
                <p className="mb-3 text-xs text-muted-foreground font-caption">Stage preview</p>
                <ol className="space-y-3">
                  {[...(selectedWorkflow.stages || [])]
                    .sort((first, second) => first.order - second.order)
                    .map((stage, index) => (
                      <li key={stage.id || stage.order} className="flex items-center gap-3">
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs text-primary font-caption font-semibold">
                          {index + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm text-foreground font-caption font-medium">{stage.name}</p>
                          <p className="truncate text-xs text-muted-foreground font-caption">{stage.assignedUserName}</p>
                        </div>
                      </li>
                    ))}
                </ol>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                type="button"
                iconName="Play"
                onClick={handleStart}
                loading={isStarting}
                disabled={!selectedWorkflowId}
              >
                Start Workflow
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default RequestWorkflowSection;
